import React, { useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import {
  setShowSignupModal,
  setShowLoginModal,
} from '../../../slices/accounts';
import styles from './SignupForm.module.scss';
import validator from './validator';
import messages from './messages';
import InputText from '../../common/InputText';
import InputPassword from '../../common/InputPassword';
import LinkButton from '../../common/LinkButton';
import Checkbox from '../../common/Checkbox';
import onSignup from '../common-shared-attributes/OnSignUp';
import ResetPasswordModal from '../../ResetPasswordModal';

const initialValues = {
  givenName: '',
  familyName: '',
  email: '',
  phoneNumber: '',
  password: '',
};

const SignupForm = ({ onCreationSuccessCallback }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [emailSubscription, setEmailSubscription] = useState(false);
  const [emailExistsError, setEmailExistsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const setAccountCreationError = (error) => {
    setErrorMessage(error);
  };

  const openLoginModal = () => {
    dispatch(setShowLoginModal({ showLoginModal: true }));
    dispatch(setShowSignupModal({ showSignupModal: false }));
  };

  const anonymousId = useSelector((state) =>
    state.cart?.anonymousId || state.order?.anonymousId);

  const {
    values, errors, handleChange, handleBlur, handleSubmit, isValid, touched,
  } =
    useFormik({
      initialValues,
      onSubmit: () => {
        onSignup(values, {
          emailExistsError,
          setEmailExistsError,
          setAccountCreationError,
          onCreationSuccessCallback,
          anonymousId,
        });
      },
      validate: (v) =>
        validator(v, {
          formatMessage,
          emailExistsError,
          setEmailExistsError,
        }),
    });

  const openTermsPrivacy = () => {
    window.open('https://purple.com/terms-and-privacy', '_blank');
  };
  const openCustomerProgamTerms = () => {
    window.open('https://purple.com/terms-and-privacy#customer-program', '_blank');
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className={styles.signupInputField}>
          <InputText
            id="givenName"
            type="text"
            name="givenName"
            label={formatMessage(messages.firstNameLabel)}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.givenName}
            isValid={isValid}
            errorMsg={touched.givenName && errors.givenNameError}
          />
        </div>
        <div className={styles.signupInputField}>
          <InputText
            id="familyName"
            type="text"
            name="familyName"
            label={formatMessage(messages.lastNameLabel)}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.familyName}
            isValid={isValid}
            errorMsg={touched.familyName && errors.familyNameError}
          />
        </div>
        <div className={styles.signupInputField}>
          <InputText
            id="email"
            type="email"
            name="email"
            label={formatMessage(messages.emailLabel)}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
            isValid={isValid}
            errorMsg={touched.email && errors.emailError}
          />
          {emailExistsError && (
            <div className={styles.emailExistsError}>
              {formatMessage(messages.emailExistsErrorMsg)}
              <span className={styles.loginLink}>
                <LinkButton
                  id="emailErrLoginLink"
                  onClick={openLoginModal}
                  isError
                  linkText={formatMessage(messages.emailExistsLoginText)}
                />
              </span>
              {formatMessage(messages.emailExistsOrText)}
              <a
                className={classnames('tdUnderline', styles.resetPasswordLink)}
                href="/account/reset-password"
              >
                {formatMessage(messages.emailExistsResetText)}
              </a>
            </div>
          )}
          {errorMessage && (
            <div className={styles['email-exists-error']}>
              {errorMessage === 'UserNotConfirmedException' && (
              <>
                <span className={styles['email-not-verifield-title']}>
                  {formatMessage(messages.emailNotVerifiedErrorTitle)}
                </span>
                  {formatMessage(messages.emailNotVerifiedError).replace('{customerEmail}', values.email)}
              </>
              )}
            </div>
          )}
        </div>
        <div className={styles.signupInputField}>
          <InputText
            id="phoneNumber"
            type="tel"
            name="phoneNumber"
            label={formatMessage(messages.phoneLabel)}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.phoneNumber}
            isValid={isValid}
            errorMsg={touched.phoneNumber && errors.phoneError}
          />
        </div>
        <div className={styles.signupInputField}>
          <InputPassword
            id="password"
            name="password"
            label={formatMessage(messages.passwordLabel)}
            handleOnChange={handleChange}
            handleOnBlur={handleBlur}
            value={values.password}
            isValid={isValid}
            errorMsg={touched.password && errors.passwordError}
          />
        </div>
        <div>
          <Checkbox
            id="emailSubscription"
            name="emailSubscription"
            label={formatMessage(messages.emailSubscriptionLabel)}
            onChange={() => setEmailSubscription(!emailSubscription)}
            checked={emailSubscription}
            isRound
          />
        </div>
        <p className={styles.termsPrivacyText}>
          <span className={styles.legalText}>
            {formatMessage(messages.emailSubscribeLegalText)}
          </span>
          <LinkButton
            id="termsPrivacyLink"
            onClick={openCustomerProgamTerms}
            linkText={formatMessage(messages.emailSubscribeLegalLink)}
          />
          <span className={styles.legalText}>
            {formatMessage(messages.emailSubscribeLegalText2)}
          </span>
          <LinkButton
            id="privacyPolicyLink"
            onClick={openTermsPrivacy}
            linkText={formatMessage(messages.emailSubscribeTermsPrivacyText)}
          />
        </p>
        <input
          type="submit"
          className={classnames('btn', styles.signupBtn)}
          value={formatMessage(messages.submitButton)}
        />
        <ResetPasswordModal />
      </form>
    </>
  );
};

SignupForm.propTypes = {
  onCreationSuccessCallback: PropTypes.func,
};

SignupForm.defaultProps = {
  onCreationSuccessCallback: () => {},
};

export default SignupForm;
